import { useCallback, useMemo, useState } from 'react';

// 定义字段依赖关系的类型，包含字段名和初始折叠状态
type FieldDependency = {
  field: string;
  isCollapsed: boolean;
};

// 定义字段依赖关系的映射类型
type FieldDependencies = {
  [key: string]: FieldDependency[];
};

// 定义Hook的配置选项类型
type UseFieldCollapseOptions = {
  formFields: string[]; // 表单中的所有字段
  collapseMap: FieldDependencies; // 字段之间的折叠关系映射，并包含初始折叠状态
};

/**
 *
 * @example
 * const [collapsedFields, { formFields, isFieldCollapsed, toggleFieldDependencies }] = useFieldCollapse({
    对应字段的折叠关系映射
    formFields: ['field1', 'field2'],
    // 映射字段展开
    collapseMap: {
      field1: [{ field: 'field3' }, { field: 'field4' }],
      field2: [{ field: 'field5' }, { field: 'field6' }],
    },
  });
 */
export const useFieldCollapse = (options: UseFieldCollapseOptions) => {
  // 初始化折叠状态，根据collapseMap中的布尔值设置初始值
  const [collapsedFields, setCollapsedFields] = useState<Set<string>>(
    new Set(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object?.entries(options.collapseMap)?.flatMap(([_, dependencies]) =>
        dependencies
          ?.filter(({ isCollapsed }) => !isCollapsed)
          ?.map(({ field }) => field),
      ),
    ),
  );

  // 切换指定字段及其依赖字段的折叠状态
  const toggleFieldDependencies = useCallback(
    (field: string) => {
      setCollapsedFields((prev) => {
        const newSet = new Set(prev); // 创建新的Set以避免直接修改状态
        const dependencies =
          options.collapseMap[field]?.map(
            ({ field: dependency }) => dependency,
          ) || [];
        // 切换依赖字段的折叠状态
        dependencies.forEach((dep) => {
          if (newSet.has(dep)) {
            newSet.delete(dep); // 如果依赖字段在Set中，则移除它（展开）
          } else {
            newSet.add(dep); // 如果依赖字段不在Set中，则添加它（折叠）
          }
        });
        return newSet;
      });
    },
    [options.collapseMap],
  );
  // 检查指定字段是否被折叠
  const isFieldCollapsed = useMemo(() => {
    if (!options?.collapseMap) return [];
    return Object?.entries(options?.collapseMap)
      // eslint-disable-next-line array-callback-return
      ?.map(([key, dependencies]) => {
        const selectKey = Array.from(collapsedFields);
        const isShow = dependencies?.some((task) =>
          selectKey.includes(task.field),
        );
        if (isShow) {
          return key;
        }
      })
      ?.filter(Boolean);
  }, [collapsedFields, options]);

  // 返回方法和状态
  return {
    isFieldCollapsed,
    formFields: options.formFields,
    collapsedFields: Array.from(collapsedFields), // 当前被折叠的字段集合
    toggleFieldDependencies, // 切换字段及其依赖字段折叠状态的方法
  };
};
